import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Shield } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { adminLogin } from '@/lib/api';

export default function AdminLoginPage() {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setBusy(true);
    try {
      await adminLogin(password);
      toast.success('Signed in as admin');
      navigate('/admin');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="flex flex-1 items-center justify-center px-4 py-10">
        <Card
          className="w-full max-w-md rounded-2xl border-border/70 bg-card p-6 shadow-[0_10px_36px_rgba(15,23,42,0.06)] sm:p-8"
          data-testid="admin-login-card"
        >
          <div className="mb-6 flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            <h1 className="font-display text-2xl font-semibold text-foreground">Admin sign-in</h1>
          </div>
          <p className="mb-6 text-sm text-muted-foreground">
            Access the UrjaPod admin dashboard to view all bill-analyzer submissions.
          </p>
          <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
            <div>
              <Label htmlFor="admin-password" className="mb-1.5 flex items-center gap-1.5 text-sm font-medium">
                <Lock className="h-4 w-4 text-muted-foreground" /> Password
              </Label>
              <Input
                id="admin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="h-11 rounded-xl"
                data-testid="admin-password-input"
                autoFocus
                required
              />
            </div>
            <Button
              type="submit"
              disabled={busy || !password}
              className="h-12 w-full rounded-xl text-base font-semibold hover:bg-[hsl(var(--brand-blue-2))]"
              data-testid="admin-login-submit-button"
            >
              {busy ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </Card>
      </main>
      <Footer />
    </div>
  );
}
